// ============================================================================
// File: StoragePanel.tsx — Spec 160 T017 · reworked by Spec 215 (US2/US5)
// Author: Peter Dobler | Created: 2026-05-31
// Feature: 215-admin-dashboard-remediation
//   FR-007: "Azure Storage" split into Fleet vs Backup storage (backup-binding
//           classification; isolated recovery vault excluded — own tile).
//   FR-008/SC-006: warehouse trend uses the headline's population; days with a
//           measured-count drop render a "measurement gap" note, not data loss.
//   FR-014: vault / secondary / growth stats added.
//   FR-024: cost subtext = effective blended tiered rate + as-of date (the flat
//           "$0.02/GB/month" literal is gone).
// Constitution: v1.23.0 | Principle XXXVII, XLV
// ============================================================================
import React from 'react';
import { DashboardPanel, Spinner, AppTooltip, CostSourceBadge, formatUsd, ErrorState, NotCollected } from '@/components/ui';
import { Sparkline } from './Sparkline';
import { formatBytes } from '@/utils/formatBytes';
import { unmeasuredSizeNote } from '@/utils/unmeasuredSize';
import type { DashboardKpis, TrendPoint } from '../../../../../shared/types/dashboard';

interface StoragePanelProps {
  data: DashboardKpis['storage'] | undefined;
  loading?: boolean;
  showCostEstimate?: boolean;
}

/**
 * 🛑 WAS A LOCAL DECIMAL FORMATTER, AND IT INFLATED EVERY FIGURE ON THIS PANEL BY 10%.
 *
 * It divided by 1e9 for GB and again by 1000 for TB, while the backend builds these byte counts
 * as `used_gb * 1024**3` (storageClassificationQueries.ts) — binary in, decimal out. Prod
 * 2026-08-14: the tile showed Backup Storage 53.01 TB and Fleet 13.23 TB where the accounts
 * actually hold 48.11 TiB and 12.03 TiB. Exactly 1024³/10⁹ = 1.0995x, on both.
 *
 * It read as plausible because it was self-consistent and the *account counts* were right, so
 * nothing looked broken — the only way to catch it was to sum the same column in SQL and compare.
 *
 * `utils/formatBytes` is the canonical Spec 157 formatter and uses 1024 throughout; its own header
 * notes that stale local copies exist and should be consolidated. This was one of them.
 */
const fmtBytes = (bytes: number): string => formatBytes(bytes);

const fmtGb = (n: number): string => `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} GB`;

/** Spec 298 T040 (Spec 238's measured-tiny rule): a measured total that rounds to 0.00 reads "< 0.01 GB", not "0.00 GB". */
const fmtTotalGb = (gb: number): string => (gb > 0 && gb < 0.005 ? '< 0.01 GB' : `${gb.toFixed(2)} GB`);

const Tile: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  trend?: TrendPoint[];
  color?: string;
  valueFmt?: (n: number) => string;
}> = ({ label, value, sub, trend, color, valueFmt }) => (
  <div className="flex flex-col rounded-[var(--radius-md)] border border-[var(--color-border)] p-3">
    <span className="block text-xs text-[var(--color-text-secondary)]">{label}</span>
    <div className="text-lg font-bold leading-tight text-[var(--color-text-primary)]">{value}</div>
    {sub && <span className="text-xs text-[var(--color-text-secondary)]">{sub}</span>}
    {trend && <Sparkline data={trend} color={color} valueFmt={valueFmt} label={label} />}
  </div>
);

/**
 * SC-006: true when the most recent trend day measured >20% fewer warehouses than
 * the series max — the dip is a MEASUREMENT gap, not storage decline.
 */
export function hasMeasurementGap(measured: TrendPoint[] | undefined): boolean {
  if (!measured || measured.length < 2) return false;
  const max = Math.max(...measured.map((p) => p.value));
  if (max <= 0) return false;
  const latest = measured[measured.length - 1].value;
  return latest < max * 0.8;
}

export const StoragePanel: React.FC<StoragePanelProps> = ({
  data, loading = false, showCostEstimate = true,
}) => {
  const t = data?.trends;
  const measurementGap = hasMeasurementGap(t?.warehouseMeasured);
  // Spec 286 T159 item 32: "N/A" only when nothing was ever sized; a total of last known sizes is still a total.
  // Spec 298 FR-004: a null share means there are none to size, which the tile says in words.
  const noLakehouses = data?.lakehouseCoverage === null;
  const noWarehouses = data?.warehouseCoverage === null;
  const noLakehouseSize = noLakehouses || (data?.lakehouseCoverage === 0 && (data?.lakehouseUnmeasured ?? 0) === 0);
  const noWarehouseSize = noWarehouses || (data?.warehouseCoverage === 0 && (data?.warehouseUnmeasured ?? 0) === 0);
  const latestMeasured = t?.warehouseMeasured?.length ? t.warehouseMeasured[t.warehouseMeasured.length - 1].value : null;
  const growth = data?.growth30dPct;

  return (
    <DashboardPanel
      title="Storage Managed"
      titleInfo="Storage under management, split by role: fleet (source data) vs backup destinations vs the isolated recovery vault, plus OneLake lakehouse/warehouse sizes and an estimated monthly cost. Hover each tile for its exact definition."
    >
      {loading ? (
        <div className="flex min-h-[120px] items-center justify-center"><Spinner size="md" /></div>
      ) : !data ? (
        // Spec 298 FR-005/FR-006: a section that was not read is said so, never shown as zeros
        <ErrorState title="Could not read the storage figures" />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {/* FR-007: the honest split — fleet (source data) vs backup destinations */}
          <AppTooltip
            title="Active storage accounts NOT referenced as backup destinations — your source/fleet data. The isolated recovery vault is excluded (own tile)."
            placement="top" className="block"
          >
            <div>
              <Tile
                label="Fleet Storage"
                value={fmtBytes(data?.fleetBytes ?? 0)}
                sub={`${data.fleetAccountCount} accounts${growth != null ? ` · ${growth >= 0 ? '+' : ''}${growth}% 30d` : ''}`}
                trend={t?.azureGb}
                color="#0ea5e9"
                valueFmt={fmtGb}
              />
            </div>
          </AppTooltip>
          <AppTooltip
            title="Accounts referenced as backup destinations or storage-map targets (per-run bindings + plans + maps)."
            placement="top" className="block"
          >
            <div>
              <Tile
                label="Backup Storage"
                value={fmtBytes(data?.backupBytes ?? 0)}
                sub={`${data.backupAccountCount} accounts`}
                trend={t?.backupGb}
                color="#8b5cf6"
                valueFmt={fmtGb}
              />
            </div>
          </AppTooltip>
          <AppTooltip
            title={
              noLakehouses ? 'No lakehouses yet.'
              : noLakehouseSize ? 'No lakehouse size data collected yet.'
              // Spec 286 T159 item 32: the merge keeps the old size of a lakehouse it could not size (Spec 238),
              // so the total COUNTS them at their last known size; it is not a floor.
              : (data?.lakehouseUnmeasured ?? 0) > 0
                ? `OneLake storage used by lakehouses. ${unmeasuredSizeNote(data?.lakehouseUnmeasured)}.`
                : 'OneLake storage used by lakehouses (from collected size snapshots).'
            }
            placement="top" className="block"
          >
            <div>
              <Tile
                label="Lakehouse (OneLake)"
                value={noLakehouses ? 'N/A'
                  : noLakehouseSize ? <NotCollected reason="No lakehouse size data collected yet." />
                  : fmtTotalGb(data.totalLakehouseGb)}
                sub={
                  <>
                    {noLakehouses ? 'no lakehouses' : `${data?.lakehouseCoverage}% measured`}
                    {/* Name the shortfall. A percentage alone tells an operator something is off;
                        the count tells them what to go and look at — and without it the total
                        reads as current when part of it is last known sizes (Spec 286 T159 item 32). */}
                    {(data?.lakehouseUnmeasured ?? 0) > 0 && (
                      <span data-testid="lakehouse-unmeasured" className="ml-1 text-[var(--color-status-warning)]">
                        · {data?.lakehouseUnmeasured} not measured
                      </span>
                    )}
                  </>
                }
                trend={t?.lakehouseGb}
                color="#6366f1"
                valueFmt={fmtGb}
              />
            </div>
          </AppTooltip>
          <AppTooltip
            title={
              noWarehouses ? 'No warehouses yet.'
              : noWarehouseSize ? 'No warehouse size data collected yet.'
              : (data?.warehouseUnmeasured ?? 0) > 0
                ? `Warehouse storage. ${unmeasuredSizeNote(data?.warehouseUnmeasured)}.`
              : measurementGap ? 'Recent trend dip reflects fewer warehouses reporting a size (measurement gap), not storage decline.'
              : 'Trend restricted to currently-active warehouses (same population as the headline).'
            }
            placement="top" className="block"
          >
            <div>
              <Tile
                label="Warehouse"
                value={noWarehouses ? 'N/A'
                  : noWarehouseSize ? <NotCollected reason="No warehouse size data collected yet." />
                  : fmtTotalGb(data.totalWarehouseGb)}
                sub={
                  <>
                    {noWarehouses ? 'no warehouses' : `${data?.warehouseCoverage}% measured`}
                    {(data?.warehouseUnmeasured ?? 0) > 0 && (
                      <span data-testid="warehouse-unmeasured" className="ml-1 text-[var(--color-status-warning)]">
                        · {data?.warehouseUnmeasured} not measured
                      </span>
                    )}
                    {measurementGap && (
                      <span data-testid="warehouse-measurement-gap" className="ml-1 text-[var(--color-status-warning)]">
                        · measurement gap ({latestMeasured} reporting)
                      </span>
                    )}
                  </>
                }
                trend={t?.warehouseGb}
                color="#f59e0b"
                valueFmt={fmtGb}
              />
            </div>
          </AppTooltip>
          {/* 🛑 Spec 270 FR-019a: the "Recovery Vault" tile is REMOVED.

              It rendered `Not configured / no vault linked` whenever `vaultBytes` was null,
              which reads as a live capability currently unused. With `ddsfcvaultprod`
              destroyed (FR-019c) and `dbo.recovery_vaults` dropped by migration 280, that
              sentence would be false in the most expensive way: an operator would believe a
              vault could be linked at any time.

              🛑 This tile is on the ADMIN DASHBOARD, not in the Backup section - the
              contract flagged it as the easiest surface in the whole removal to miss, and
              neither typecheck reaches it from the backend change that feeds it. */}
          {showCostEstimate ? (
            <div>
              <Tile
                label="Storage Cost (30d)"
                value={data?.estimatedMonthlyCostUsd != null ? formatUsd(data.estimatedMonthlyCostUsd) : '—'}
                sub="daily spend · last 60d"
                trend={t?.costUsd}
                color="#22c55e"
                valueFmt={(n) => formatUsd(n)}
              />
              <div className="mt-1"><CostSourceBadge source="actual" window="Last 30 days" /></div>
            </div>
          ) : (
            <div /> /* placeholder to keep the grid balanced */
          )}
        </div>
      )}
    </DashboardPanel>
  );
};

export default StoragePanel;
